import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { HeroImageUpload } from './HeroImageUpload';
import { DescriptionSections } from './DescriptionSections';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

type Brand = {
  id: string;
  name: string;
};

interface CustomReportFormProps {
  reportId?: string;
  brands: Brand[];
  initialData?: any;
  onSave: (data: any) => Promise<void>;
  saving?: boolean;
}

export function CustomReportForm({ reportId, brands, initialData, onSave, saving = false }: CustomReportFormProps) {
  const [formData, setFormData] = useState({
    // Basic Information
    campaignName: '',
    brandId: '',
    reportDate: '',
    campaignStartDate: '',
    campaignEndDate: '',
    daysElapsed: '',
    daysRemaining: '',
    campaignId: '',
    
    // Custom Campaign Details
    location: '',
    duration: '',
    durationUnit: 'months' as 'months' | 'weeks' | 'days',
    productName: '',
    productPrice: '',
    productStructure: '',
    
    // Hero Images
    heroImages: [] as string[],
    
    // Media Performance - Impressions
    totalImpressions: '',
    averageImpressionsPerMachine: '',
    targetImpressions: '',
    
    // Media Performance - Total Ad Playout
    doohAdPlays: '',
    averagePlaysPerMachine: '',
    totalScreenTimeMinutes: '',
    
    // Media Performance - Digital Engagement
    homepageBannerImpressions: '',
    categoryBannerImpressions: '',
    clickThroughRate: '',
    productPageViews: '',
    engagementRate: '',
    
    // Footfall & Visibility (New)
    footfallZoneMonthly: '',
    footfallZoneTotal: '',
    screenImpressionsMonthly: '',
    screenImpressionsTotal: '',
    adPlayoutsRotating: '',
    adPlayoutsTotalPlays: '',
    
    // Engagement Metrics (New)
    screenClicksMonthly: '',
    screenClicksTotal: '',
    browsingSessionsMonthly: '',
    browsingSessionsTotal: '',
    averageBrowseTimeSeconds: '',
    footfallToBrowseRate: '',
    
    // Sales Performance
    overallSalesPercentIncrease: '',
    products: [] as Array<{ id: string; name: string; percentIncrease: string }>,
    
    // Conversion Performance (New)
    conversionFootfallToPurchase: '',
    conversionBrowsingToPurchase: '',
    averageSellingPrice: '',
    monthlyUnitsSold: '',
    totalUnits: '',
    
    // Revenue Performance (New)
    initialBoxRevenueMonthly: '',
    initialBoxRevenueTotal: '',
    subscriptionRevenueMonthly: '',
    subscriptionRevenueTotal: '',
    totalCampaignRevenue: '',
    totalTransactionValueProcessed: '',
    
    // Retention & Churn (New)
    threeMonthCompletionRate: '',
    churnRate: '',
    autoRenewTakeUp: '',
    averageLifetimeValue: '',
    
    // Store Halo Effect (New)
    footfallUplift: '',
    giftCardRedemptionInStore: '',
    sameDayCrossPurchase: '',
    estimatedIncrementalStoreRevenue: '',
    
    // Sales by Time of Day (New)
    salesTime10am1pm: '',
    salesTime1pm4pm: '',
    salesTime4pm7pm: '',
    salesTime7pm9pm: '',
    
    // Customer Feedback (New)
    overallExperienceScore: '',
    easeOfPurchaseScore: '',
    giftAppealRating: '',
    wouldRecommend: '',
    commonFeedbackThemes: '',
    
    // Category Performance - Market Share by Product
    preCampaignMarketShareProducts: [] as Array<{ id: string; name: string; marketShare: string }>,
    postCampaignMarketShareProducts: [] as Array<{ id: string; name: string; marketShare: string }>,
    
    // Demographics - Gender Split
    genderMale: '',
    genderFemale: '',
    
    // Demographics - Age Distribution
    age1824: '',
    age2534: '',
    age3544: '',
    age4554: '',
    age55Plus: '',
    
    // Demographics - Purchase Timing
    peakHours: '',
    peakDays: '',
    purchaseTimingMorning: '',
    purchaseTimingAfternoon: '',
    purchaseTimingEvening: '',
    
    // Campaign Pacing & Budget
    totalCampaignBudget: '',
    percentCampaignElapsed: '',
    percentBudgetUtilized: '',
    pacingStatus: 'on-track' as 'on-track' | 'ahead' | 'behind',
    
    // Description Sections
    descriptionSections: {
      executiveSummary: '',
      keyInsights: '',
      recommendations: '',
      strategicTakeaways: '',
      customerFeedback: '',
    },
  });

  useEffect(() => {
    if (initialData) {
      const data = {
        ...initialData,
        heroImages: initialData.heroImages ? (typeof initialData.heroImages === 'string' ? JSON.parse(initialData.heroImages) : initialData.heroImages) : [],
        descriptionSections: initialData.descriptionSections || {
          executiveSummary: '',
          keyInsights: '',
          recommendations: '',
          strategicTakeaways: '',
          customerFeedback: '',
        },
        products: initialData.products && Array.isArray(initialData.products) 
          ? initialData.products.map((p: any, i: number) => ({
              id: p.id || `product-${i}-${Date.now()}`,
              name: p.name || '',
              percentIncrease: p.percentIncrease || '',
            }))
          : [],
        preCampaignMarketShareProducts: initialData.preCampaignMarketShareProducts && Array.isArray(initialData.preCampaignMarketShareProducts)
          ? initialData.preCampaignMarketShareProducts.map((p: any, i: number) => ({
              id: p.id || `pre-market-share-${i}-${Date.now()}`,
              name: p.name || '',
              marketShare: p.marketShare || '',
            }))
          : [],
        postCampaignMarketShareProducts: initialData.postCampaignMarketShareProducts && Array.isArray(initialData.postCampaignMarketShareProducts)
          ? initialData.postCampaignMarketShareProducts.map((p: any, i: number) => ({
              id: p.id || `post-market-share-${i}-${Date.now()}`,
              name: p.name || '',
              marketShare: p.marketShare || '',
            }))
          : [],
      };
      setFormData(data);
    }
  }, [initialData]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateDays = () => {
    if (formData.campaignStartDate && formData.campaignEndDate) {
      const start = new Date(formData.campaignStartDate);
      const end = new Date(formData.campaignEndDate);
      const today = formData.reportDate ? new Date(formData.reportDate) : new Date();
      const diffTime = Math.abs(today.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      const remaining = totalDays - diffDays;
      
      if (!formData.daysElapsed || formData.daysElapsed === '') {
        handleChange('daysElapsed', diffDays.toString());
      }
      if (!formData.daysRemaining || formData.daysRemaining === '') {
        handleChange('daysRemaining', remaining > 0 ? remaining.toString() : '0');
      }
    }
  };

  useEffect(() => {
    calculateDays();
  }, [formData.campaignStartDate, formData.campaignEndDate, formData.reportDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Hero Images Section */}
      <HeroImageUpload
        images={formData.heroImages}
        onChange={(images) => handleChange('heroImages', images)}
      />

      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="basic">Basic Info</TabsTrigger>
          <TabsTrigger value="campaign">Campaign Details</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="descriptions">Descriptions</TabsTrigger>
        </TabsList>

        {/* Basic Information Tab */}
        <TabsContent value="basic" className="space-y-6">
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Campaign Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="campaignName">Campaign Name</Label>
                  <Input
                    id="campaignName"
                    value={formData.campaignName}
                    onChange={(e) => handleChange('campaignName', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="brandId">Brand</Label>
                  <Select value={formData.brandId} onValueChange={(value) => handleChange('brandId', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a brand" />
                    </SelectTrigger>
                    <SelectContent>
                      {brands.map(brand => (
                        <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="reportDate">Report Date</Label>
                  <Input
                    id="reportDate"
                    type="date"
                    value={formData.reportDate}
                    onChange={(e) => handleChange('reportDate', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="campaignStartDate">Campaign Start Date</Label>
                  <Input
                    id="campaignStartDate"
                    type="date"
                    value={formData.campaignStartDate}
                    onChange={(e) => handleChange('campaignStartDate', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="campaignEndDate">Campaign End Date</Label>
                  <Input
                    id="campaignEndDate"
                    type="date"
                    value={formData.campaignEndDate}
                    onChange={(e) => handleChange('campaignEndDate', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="daysElapsed">Days Elapsed</Label>
                  <Input
                    id="daysElapsed"
                    type="number"
                    value={formData.daysElapsed}
                    onChange={(e) => handleChange('daysElapsed', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="daysRemaining">Days Remaining</Label>
                  <Input
                    id="daysRemaining"
                    type="number"
                    value={formData.daysRemaining}
                    onChange={(e) => handleChange('daysRemaining', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="campaignId">Campaign ID/Reference</Label>
                  <Input
                    id="campaignId"
                    value={formData.campaignId}
                    onChange={(e) => handleChange('campaignId', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Campaign Details Tab */}
        <TabsContent value="campaign" className="space-y-6">
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Campaign Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    placeholder="e.g., Coal Drops Yard, King's Cross, London"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="duration">Duration</Label>
                    <Input
                      id="duration"
                      type="number"
                      value={formData.duration}
                      onChange={(e) => handleChange('duration', e.target.value)}
                      placeholder="e.g., 6"
                    />
                  </div>
                  <div>
                    <Label htmlFor="durationUnit">Unit</Label>
                    <Select value={formData.durationUnit} onValueChange={(value: 'months' | 'weeks' | 'days') => handleChange('durationUnit', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="months">Months</SelectItem>
                        <SelectItem value="weeks">Weeks</SelectItem>
                        <SelectItem value="days">Days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="productName">Product Name</Label>
                  <Input
                    id="productName"
                    value={formData.productName}
                    onChange={(e) => handleChange('productName', e.target.value)}
                    placeholder="e.g., Lush Experience Box"
                  />
                </div>
                <div>
                  <Label htmlFor="productPrice">Product Price (£)</Label>
                  <Input
                    id="productPrice"
                    type="number"
                    step="0.01"
                    value={formData.productPrice}
                    onChange={(e) => handleChange('productPrice', e.target.value)}
                    placeholder="e.g., 120.00"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="productStructure">Product Structure</Label>
                <Textarea
                  id="productStructure"
                  value={formData.productStructure}
                  onChange={(e) => handleChange('productStructure', e.target.value)}
                  rows={4}
                  placeholder="e.g., Month 1: Physical premium gift box dispensed from machine. Months 2-3: Subscription with custom scent selection. Auto-renew from Month 4."
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-6">
          {/* Media Performance */}
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Media Performance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <CardTitle className="text-lg mb-4">Impressions (To-Date)</CardTitle>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="totalImpressions">Total Impressions</Label>
                    <Input
                      id="totalImpressions"
                      type="number"
                      value={formData.totalImpressions}
                      onChange={(e) => handleChange('totalImpressions', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="averageImpressionsPerMachine">Average Impressions per Machine</Label>
                    <Input
                      id="averageImpressionsPerMachine"
                      type="number"
                      value={formData.averageImpressionsPerMachine}
                      onChange={(e) => handleChange('averageImpressionsPerMachine', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="targetImpressions">Target Impressions</Label>
                    <Input
                      id="targetImpressions"
                      type="number"
                      value={formData.targetImpressions}
                      onChange={(e) => handleChange('targetImpressions', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <CardTitle className="text-lg mb-4">Total Ad Playout (To-Date)</CardTitle>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="doohAdPlays">DOOH Ad Plays</Label>
                    <Input
                      id="doohAdPlays"
                      type="number"
                      value={formData.doohAdPlays}
                      onChange={(e) => handleChange('doohAdPlays', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="averagePlaysPerMachine">Average Plays per Machine</Label>
                    <Input
                      id="averagePlaysPerMachine"
                      type="number"
                      value={formData.averagePlaysPerMachine}
                      onChange={(e) => handleChange('averagePlaysPerMachine', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="totalScreenTimeMinutes">Total Screen Time (hours)</Label>
                    <Input
                      id="totalScreenTimeMinutes"
                      type="number"
                      value={formData.totalScreenTimeMinutes}
                      onChange={(e) => handleChange('totalScreenTimeMinutes', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <CardTitle className="text-lg mb-4">Digital Engagement (To-Date)</CardTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="homepageBannerImpressions">Homepage Banner Impressions</Label>
                    <Input
                      id="homepageBannerImpressions"
                      type="number"
                      value={formData.homepageBannerImpressions}
                      onChange={(e) => handleChange('homepageBannerImpressions', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="categoryBannerImpressions">Category Banner Impressions</Label>
                    <Input
                      id="categoryBannerImpressions"
                      type="number"
                      value={formData.categoryBannerImpressions}
                      onChange={(e) => handleChange('categoryBannerImpressions', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="clickThroughRate">Click-Through Rate (CTR) %</Label>
                    <Input
                      id="clickThroughRate"
                      type="number"
                      step="0.01"
                      value={formData.clickThroughRate}
                      onChange={(e) => handleChange('clickThroughRate', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="productPageViews">Product Page Views</Label>
                    <Input
                      id="productPageViews"
                      type="number"
                      value={formData.productPageViews}
                      onChange={(e) => handleChange('productPageViews', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="engagementRate">Engagement Rate %</Label>
                    <Input
                      id="engagementRate"
                      type="number"
                      step="0.01"
                      value={formData.engagementRate}
                      onChange={(e) => handleChange('engagementRate', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sales Performance */}
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Sales Performance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="overallSalesPercentIncrease">Overall Sales % Increase</Label>
                <Input
                  id="overallSalesPercentIncrease"
                  type="number"
                  step="0.01"
                  value={formData.overallSalesPercentIncrease}
                  onChange={(e) => handleChange('overallSalesPercentIncrease', e.target.value)}
                  placeholder="e.g., 17.5"
                />
              </div>
              
              <div className="space-y-3">
                <Label>Products</Label>
                {formData.products.map((product, index) => (
                  <div key={product.id} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label htmlFor={`product-name-${product.id}`}>Product Name</Label>
                      <Input
                        id={`product-name-${product.id}`}
                        value={product.name}
                        onChange={(e) => {
                          const updatedProducts = [...formData.products];
                          updatedProducts[index].name = e.target.value;
                          handleChange('products', updatedProducts);
                        }}
                        placeholder="e.g., Redbull Original"
                      />
                    </div>
                    <div className="w-32">
                      <Label htmlFor={`product-increase-${product.id}`}>% Increase</Label>
                      <Input
                        id={`product-increase-${product.id}`}
                        type="number"
                        step="0.01"
                        value={product.percentIncrease}
                        onChange={(e) => {
                          const updatedProducts = [...formData.products];
                          updatedProducts[index].percentIncrease = e.target.value;
                          handleChange('products', updatedProducts);
                        }}
                        placeholder="%"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        const updatedProducts = formData.products.filter((_, i) => i !== index);
                        handleChange('products', updatedProducts);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const newProduct = {
                      id: `product-${Date.now()}-${Math.random()}`,
                      name: '',
                      percentIncrease: '',
                    };
                    handleChange('products', [...formData.products, newProduct]);
                  }}
                >
                  + Add Product
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Category Performance */}
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Category Performance - Market Share by Product</CardTitle>
              <CardDescription>Enter market share percentages for each product. Combined percentages represent total brand market share.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <CardTitle className="text-lg mb-4">Pre-Campaign Market Share</CardTitle>
                <div className="space-y-3">
                  {formData.preCampaignMarketShareProducts.map((product, index) => (
                    <div key={product.id} className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Label htmlFor={`pre-product-name-${product.id}`}>Product Name</Label>
                        <Input
                          id={`pre-product-name-${product.id}`}
                          value={product.name}
                          onChange={(e) => {
                            const updatedProducts = [...formData.preCampaignMarketShareProducts];
                            updatedProducts[index].name = e.target.value;
                            handleChange('preCampaignMarketShareProducts', updatedProducts);
                          }}
                          placeholder="e.g., Redbull Original"
                        />
                      </div>
                      <div className="w-32">
                        <Label htmlFor={`pre-market-share-${product.id}`}>Market Share %</Label>
                        <Input
                          id={`pre-market-share-${product.id}`}
                          type="number"
                          step="0.01"
                          value={product.marketShare}
                          onChange={(e) => {
                            const updatedProducts = [...formData.preCampaignMarketShareProducts];
                            updatedProducts[index].marketShare = e.target.value;
                            handleChange('preCampaignMarketShareProducts', updatedProducts);
                          }}
                          placeholder="%"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          const updatedProducts = formData.preCampaignMarketShareProducts.filter((_, i) => i !== index);
                          handleChange('preCampaignMarketShareProducts', updatedProducts);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const newProduct = {
                        id: `pre-market-share-${Date.now()}-${Math.random()}`,
                        name: '',
                        marketShare: '',
                      };
                      handleChange('preCampaignMarketShareProducts', [...formData.preCampaignMarketShareProducts, newProduct]);
                    }}
                  >
                    + Add Pre-Campaign Product
                  </Button>
                </div>
              </div>

              <Separator />

              <div>
                <CardTitle className="text-lg mb-4">Post-Campaign Market Share</CardTitle>
                <div className="space-y-3">
                  {formData.postCampaignMarketShareProducts.map((product, index) => (
                    <div key={product.id} className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Label htmlFor={`post-product-name-${product.id}`}>Product Name</Label>
                        <Input
                          id={`post-product-name-${product.id}`}
                          value={product.name}
                          onChange={(e) => {
                            const updatedProducts = [...formData.postCampaignMarketShareProducts];
                            updatedProducts[index].name = e.target.value;
                            handleChange('postCampaignMarketShareProducts', updatedProducts);
                          }}
                          placeholder="e.g., Redbull Original"
                        />
                      </div>
                      <div className="w-32">
                        <Label htmlFor={`post-market-share-${product.id}`}>Market Share %</Label>
                        <Input
                          id={`post-market-share-${product.id}`}
                          type="number"
                          step="0.01"
                          value={product.marketShare}
                          onChange={(e) => {
                            const updatedProducts = [...formData.postCampaignMarketShareProducts];
                            updatedProducts[index].marketShare = e.target.value;
                            handleChange('postCampaignMarketShareProducts', updatedProducts);
                          }}
                          placeholder="%"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          const updatedProducts = formData.postCampaignMarketShareProducts.filter((_, i) => i !== index);
                          handleChange('postCampaignMarketShareProducts', updatedProducts);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const newProduct = {
                        id: `post-market-share-${Date.now()}-${Math.random()}`,
                        name: '',
                        marketShare: '',
                      };
                      handleChange('postCampaignMarketShareProducts', [...formData.postCampaignMarketShareProducts, newProduct]);
                    }}
                  >
                    + Add Post-Campaign Product
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Campaign Pacing & Budget */}
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Campaign Pacing & Budget</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="totalCampaignBudget">Total Campaign Budget (£)</Label>
                  <Input
                    id="totalCampaignBudget"
                    type="number"
                    step="0.01"
                    value={formData.totalCampaignBudget}
                    onChange={(e) => handleChange('totalCampaignBudget', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="percentCampaignElapsed">% of Campaign Elapsed</Label>
                  <Input
                    id="percentCampaignElapsed"
                    type="number"
                    step="0.01"
                    value={formData.percentCampaignElapsed}
                    onChange={(e) => handleChange('percentCampaignElapsed', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="percentBudgetUtilized">% of Budget Utilized</Label>
                  <Input
                    id="percentBudgetUtilized"
                    type="number"
                    step="0.01"
                    value={formData.percentBudgetUtilized}
                    onChange={(e) => handleChange('percentBudgetUtilized', e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Pacing Status</Label>
                <RadioGroup
                  value={formData.pacingStatus}
                  onValueChange={(value) => handleChange('pacingStatus', value as 'on-track' | 'ahead' | 'behind')}
                  className="flex gap-6 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="on-track" id="on-track" />
                    <Label htmlFor="on-track" className="cursor-pointer">On Track</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ahead" id="ahead" />
                    <Label htmlFor="ahead" className="cursor-pointer">Ahead</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="behind" id="behind" />
                    <Label htmlFor="behind" className="cursor-pointer">Behind</Label>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Engagement Tab */}
        <TabsContent value="engagement" className="space-y-6">
          {/* Footfall & Visibility */}
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Footfall & Visibility</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="footfallZoneMonthly">Footfall (Zone) - Monthly Average</Label>
                  <Input
                    id="footfallZoneMonthly"
                    type="number"
                    value={formData.footfallZoneMonthly}
                    onChange={(e) => handleChange('footfallZoneMonthly', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="footfallZoneTotal">Footfall (Zone) - Total</Label>
                  <Input
                    id="footfallZoneTotal"
                    type="number"
                    value={formData.footfallZoneTotal}
                    onChange={(e) => handleChange('footfallZoneTotal', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="screenImpressionsMonthly">Screen Impressions - Monthly Average</Label>
                  <Input
                    id="screenImpressionsMonthly"
                    type="number"
                    value={formData.screenImpressionsMonthly}
                    onChange={(e) => handleChange('screenImpressionsMonthly', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="screenImpressionsTotal">Screen Impressions - Total</Label>
                  <Input
                    id="screenImpressionsTotal"
                    type="number"
                    value={formData.screenImpressionsTotal}
                    onChange={(e) => handleChange('screenImpressionsTotal', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="adPlayoutsRotating">Ad Playouts - Number of Ads Rotating</Label>
                  <Input
                    id="adPlayoutsRotating"
                    type="number"
                    value={formData.adPlayoutsRotating}
                    onChange={(e) => handleChange('adPlayoutsRotating', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="adPlayoutsTotalPlays">Ad Playouts - Total Plays</Label>
                  <Input
                    id="adPlayoutsTotalPlays"
                    type="number"
                    value={formData.adPlayoutsTotalPlays}
                    onChange={(e) => handleChange('adPlayoutsTotalPlays', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Engagement Metrics */}
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Engagement Metrics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="screenClicksMonthly">Screen Clicks/Interactions - Monthly Average</Label>
                  <Input
                    id="screenClicksMonthly"
                    type="number"
                    value={formData.screenClicksMonthly}
                    onChange={(e) => handleChange('screenClicksMonthly', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="screenClicksTotal">Screen Clicks/Interactions - Total</Label>
                  <Input
                    id="screenClicksTotal"
                    type="number"
                    value={formData.screenClicksTotal}
                    onChange={(e) => handleChange('screenClicksTotal', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="browsingSessionsMonthly">Browsing Sessions - Monthly Average</Label>
                  <Input
                    id="browsingSessionsMonthly"
                    type="number"
                    value={formData.browsingSessionsMonthly}
                    onChange={(e) => handleChange('browsingSessionsMonthly', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="browsingSessionsTotal">Browsing Sessions - Total</Label>
                  <Input
                    id="browsingSessionsTotal"
                    type="number"
                    value={formData.browsingSessionsTotal}
                    onChange={(e) => handleChange('browsingSessionsTotal', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="averageBrowseTimeSeconds">Average Browse Time (seconds)</Label>
                  <Input
                    id="averageBrowseTimeSeconds"
                    type="number"
                    value={formData.averageBrowseTimeSeconds}
                    onChange={(e) => handleChange('averageBrowseTimeSeconds', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="footfallToBrowseRate">Footfall → Browse Rate (%)</Label>
                  <Input
                    id="footfallToBrowseRate"
                    type="number"
                    step="0.01"
                    value={formData.footfallToBrowseRate}
                    onChange={(e) => handleChange('footfallToBrowseRate', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conversion Performance */}
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Conversion Performance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="conversionFootfallToPurchase">Conversion: Footfall → Purchase (%)</Label>
                  <Input
                    id="conversionFootfallToPurchase"
                    type="number"
                    step="0.01"
                    value={formData.conversionFootfallToPurchase}
                    onChange={(e) => handleChange('conversionFootfallToPurchase', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="conversionBrowsingToPurchase">Conversion: Browsing → Purchase (%)</Label>
                  <Input
                    id="conversionBrowsingToPurchase"
                    type="number"
                    step="0.01"
                    value={formData.conversionBrowsingToPurchase}
                    onChange={(e) => handleChange('conversionBrowsingToPurchase', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="averageSellingPrice">Average Selling Price (£)</Label>
                  <Input
                    id="averageSellingPrice"
                    type="number"
                    step="0.01"
                    value={formData.averageSellingPrice}
                    onChange={(e) => handleChange('averageSellingPrice', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="monthlyUnitsSold">Monthly Units Sold</Label>
                  <Input
                    id="monthlyUnitsSold"
                    type="number"
                    value={formData.monthlyUnitsSold}
                    onChange={(e) => handleChange('monthlyUnitsSold', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="totalUnits">Total Units</Label>
                  <Input
                    id="totalUnits"
                    type="number"
                    value={formData.totalUnits}
                    onChange={(e) => handleChange('totalUnits', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Demographics */}
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Demographics Insights</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <CardTitle className="text-lg mb-4">Gender Split</CardTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="genderMale">Male %</Label>
                    <Input
                      id="genderMale"
                      type="number"
                      step="0.01"
                      value={formData.genderMale}
                      onChange={(e) => handleChange('genderMale', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="genderFemale">Female %</Label>
                    <Input
                      id="genderFemale"
                      type="number"
                      step="0.01"
                      value={formData.genderFemale}
                      onChange={(e) => handleChange('genderFemale', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <CardTitle className="text-lg mb-4">Age Distribution</CardTitle>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div>
                    <Label htmlFor="age1824">18-24 %</Label>
                    <Input
                      id="age1824"
                      type="number"
                      step="0.01"
                      value={formData.age1824}
                      onChange={(e) => handleChange('age1824', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="age2534">25-34 %</Label>
                    <Input
                      id="age2534"
                      type="number"
                      step="0.01"
                      value={formData.age2534}
                      onChange={(e) => handleChange('age2534', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="age3544">35-44 %</Label>
                    <Input
                      id="age3544"
                      type="number"
                      step="0.01"
                      value={formData.age3544}
                      onChange={(e) => handleChange('age3544', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="age4554">45-54 %</Label>
                    <Input
                      id="age4554"
                      type="number"
                      step="0.01"
                      value={formData.age4554}
                      onChange={(e) => handleChange('age4554', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="age55Plus">55+ %</Label>
                    <Input
                      id="age55Plus"
                      type="number"
                      step="0.01"
                      value={formData.age55Plus}
                      onChange={(e) => handleChange('age55Plus', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <CardTitle className="text-lg mb-4">Purchase Timing</CardTitle>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div>
                    <Label htmlFor="peakHours">Peak Hours</Label>
                    <Input
                      id="peakHours"
                      value={formData.peakHours}
                      onChange={(e) => handleChange('peakHours', e.target.value)}
                      placeholder="e.g., 12pm-2pm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="peakDays">Peak Days</Label>
                    <Input
                      id="peakDays"
                      value={formData.peakDays}
                      onChange={(e) => handleChange('peakDays', e.target.value)}
                      placeholder="e.g., Monday, Friday"
                    />
                  </div>
                  <div>
                    <Label htmlFor="purchaseTimingMorning">Morning (6am-12pm) %</Label>
                    <Input
                      id="purchaseTimingMorning"
                      type="number"
                      step="0.01"
                      value={formData.purchaseTimingMorning}
                      onChange={(e) => handleChange('purchaseTimingMorning', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="purchaseTimingAfternoon">Afternoon (12pm-6pm) %</Label>
                    <Input
                      id="purchaseTimingAfternoon"
                      type="number"
                      step="0.01"
                      value={formData.purchaseTimingAfternoon}
                      onChange={(e) => handleChange('purchaseTimingAfternoon', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="purchaseTimingEvening">Evening (6pm-12am) %</Label>
                    <Input
                      id="purchaseTimingEvening"
                      type="number"
                      step="0.01"
                      value={formData.purchaseTimingEvening}
                      onChange={(e) => handleChange('purchaseTimingEvening', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sales by Time of Day */}
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Sales by Time of Day</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="salesTime10am1pm">10am-1pm (%)</Label>
                  <Input
                    id="salesTime10am1pm"
                    type="number"
                    step="0.01"
                    value={formData.salesTime10am1pm}
                    onChange={(e) => handleChange('salesTime10am1pm', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="salesTime1pm4pm">1pm-4pm (%)</Label>
                  <Input
                    id="salesTime1pm4pm"
                    type="number"
                    step="0.01"
                    value={formData.salesTime1pm4pm}
                    onChange={(e) => handleChange('salesTime1pm4pm', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="salesTime4pm7pm">4pm-7pm (%)</Label>
                  <Input
                    id="salesTime4pm7pm"
                    type="number"
                    step="0.01"
                    value={formData.salesTime4pm7pm}
                    onChange={(e) => handleChange('salesTime4pm7pm', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="salesTime7pm9pm">7pm-9pm (%)</Label>
                  <Input
                    id="salesTime7pm9pm"
                    type="number"
                    step="0.01"
                    value={formData.salesTime7pm9pm}
                    onChange={(e) => handleChange('salesTime7pm9pm', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer Feedback */}
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Customer Feedback</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="overallExperienceScore">Overall Experience Score (1-5)</Label>
                  <Input
                    id="overallExperienceScore"
                    type="number"
                    step="0.1"
                    min="1"
                    max="5"
                    value={formData.overallExperienceScore}
                    onChange={(e) => handleChange('overallExperienceScore', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="easeOfPurchaseScore">Ease of Purchase Score (1-5)</Label>
                  <Input
                    id="easeOfPurchaseScore"
                    type="number"
                    step="0.1"
                    min="1"
                    max="5"
                    value={formData.easeOfPurchaseScore}
                    onChange={(e) => handleChange('easeOfPurchaseScore', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="giftAppealRating">Gift Appeal Rating (1-5)</Label>
                  <Input
                    id="giftAppealRating"
                    type="number"
                    step="0.1"
                    min="1"
                    max="5"
                    value={formData.giftAppealRating}
                    onChange={(e) => handleChange('giftAppealRating', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="wouldRecommend">Would Recommend (%)</Label>
                  <Input
                    id="wouldRecommend"
                    type="number"
                    step="0.01"
                    value={formData.wouldRecommend}
                    onChange={(e) => handleChange('wouldRecommend', e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="commonFeedbackThemes">Common Feedback Themes</Label>
                <Textarea
                  id="commonFeedbackThemes"
                  value={formData.commonFeedbackThemes}
                  onChange={(e) => handleChange('commonFeedbackThemes', e.target.value)}
                  rows={4}
                  placeholder="e.g., 'Best last-minute premium gift solution.', 'Loved choosing scent in Month 2.'"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Revenue Tab */}
        <TabsContent value="revenue" className="space-y-6">
          {/* Revenue Performance */}
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Revenue Performance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="initialBoxRevenueMonthly">Initial Box Revenue - Monthly (£)</Label>
                  <Input
                    id="initialBoxRevenueMonthly"
                    type="number"
                    step="0.01"
                    value={formData.initialBoxRevenueMonthly}
                    onChange={(e) => handleChange('initialBoxRevenueMonthly', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="initialBoxRevenueTotal">Initial Box Revenue - Total (£)</Label>
                  <Input
                    id="initialBoxRevenueTotal"
                    type="number"
                    step="0.01"
                    value={formData.initialBoxRevenueTotal}
                    onChange={(e) => handleChange('initialBoxRevenueTotal', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="subscriptionRevenueMonthly">Subscription Revenue - Monthly (£)</Label>
                  <Input
                    id="subscriptionRevenueMonthly"
                    type="number"
                    step="0.01"
                    value={formData.subscriptionRevenueMonthly}
                    onChange={(e) => handleChange('subscriptionRevenueMonthly', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="subscriptionRevenueTotal">Subscription Revenue - Total (£)</Label>
                  <Input
                    id="subscriptionRevenueTotal"
                    type="number"
                    step="0.01"
                    value={formData.subscriptionRevenueTotal}
                    onChange={(e) => handleChange('subscriptionRevenueTotal', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="totalCampaignRevenue">Total Campaign Revenue (£)</Label>
                  <Input
                    id="totalCampaignRevenue"
                    type="number"
                    step="0.01"
                    value={formData.totalCampaignRevenue}
                    onChange={(e) => handleChange('totalCampaignRevenue', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="totalTransactionValueProcessed">Total Transaction Value Processed (£)</Label>
                  <Input
                    id="totalTransactionValueProcessed"
                    type="number"
                    step="0.01"
                    value={formData.totalTransactionValueProcessed}
                    onChange={(e) => handleChange('totalTransactionValueProcessed', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Retention & Churn */}
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Retention & Churn</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="threeMonthCompletionRate">3-Month Completion Rate (%)</Label>
                  <Input
                    id="threeMonthCompletionRate"
                    type="number"
                    step="0.01"
                    value={formData.threeMonthCompletionRate}
                    onChange={(e) => handleChange('threeMonthCompletionRate', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="churnRate">Churn Rate (%)</Label>
                  <Input
                    id="churnRate"
                    type="number"
                    step="0.01"
                    value={formData.churnRate}
                    onChange={(e) => handleChange('churnRate', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="autoRenewTakeUp">Auto-Renew Take-Up (% of retained base)</Label>
                  <Input
                    id="autoRenewTakeUp"
                    type="number"
                    step="0.01"
                    value={formData.autoRenewTakeUp}
                    onChange={(e) => handleChange('autoRenewTakeUp', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="averageLifetimeValue">Average Lifetime Value per Customer (£)</Label>
                  <Input
                    id="averageLifetimeValue"
                    type="number"
                    step="0.01"
                    value={formData.averageLifetimeValue}
                    onChange={(e) => handleChange('averageLifetimeValue', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Store Halo Effect */}
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Store Halo Effect</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="footfallUplift">Footfall Uplift (% vs baseline)</Label>
                  <Input
                    id="footfallUplift"
                    type="number"
                    step="0.01"
                    value={formData.footfallUplift}
                    onChange={(e) => handleChange('footfallUplift', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="giftCardRedemptionInStore">Gift Card Redemption In-Store (%)</Label>
                  <Input
                    id="giftCardRedemptionInStore"
                    type="number"
                    step="0.01"
                    value={formData.giftCardRedemptionInStore}
                    onChange={(e) => handleChange('giftCardRedemptionInStore', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="sameDayCrossPurchase">Same-Day Cross Purchase (%)</Label>
                  <Input
                    id="sameDayCrossPurchase"
                    type="number"
                    step="0.01"
                    value={formData.sameDayCrossPurchase}
                    onChange={(e) => handleChange('sameDayCrossPurchase', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="estimatedIncrementalStoreRevenue">Estimated Incremental Store Revenue (£)</Label>
                  <Input
                    id="estimatedIncrementalStoreRevenue"
                    type="number"
                    step="0.01"
                    value={formData.estimatedIncrementalStoreRevenue}
                    onChange={(e) => handleChange('estimatedIncrementalStoreRevenue', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Descriptions Tab */}
        <TabsContent value="descriptions" className="space-y-6">
          <DescriptionSections
            sections={formData.descriptionSections}
            onChange={(sections) => handleChange('descriptionSections', sections)}
          />
        </TabsContent>
      </Tabs>

      <div className="flex justify-end gap-4 pt-6">
        <Button type="submit" disabled={saving} style={{ backgroundColor: '#183df7', boxShadow: '0 2px 4px rgba(24, 61, 247, 0.3)' }}>
          {saving ? 'Saving...' : 'Save Report'}
        </Button>
      </div>
    </form>
  );
}
